import React, { useEffect, useState } from "react";
import { getProfilePic } from "../../../api/Treatments/PatientDatabase/PatientDatabase";
import { displayDate } from "../../../utils/date";
import { useSelector } from "react-redux";
import { tagColors } from "../../_data/data";

/**
 * Patient card.
 *
 * Mobile (< lg): compact — avatar with the patient name and ID beside it,
 * then a 2-column grid of label-over-value cells (mobile, gender, DOB,
 * age). Keeps the card short on long pages like the medicine bill builder
 * while giving every value enough width to stay on one line.
 *
 * Tablet + desktop (≥ lg): unchanged — generous 122px avatar + three
 * column groups separated by gaps, same look as before.
 */
const Profile = ({
  patientProfile,
  hideProfilePic = false,
  showTag = true,
  showPatientType = true,
  showSpecializationType = true,
}) => {
  const [img, setImg] = useState("");
  const hospitalInfo = useSelector((state) => state.hospitalInfo);
  const { configuration } = hospitalInfo;

  useEffect(() => {
    getProfilePic(patientProfile?._id).then((response) => {
      setImg(response);
    });
  }, [patientProfile]);

  // Label-over-value cell used by the phone layout. Stacking the label
  // above the value gives the value the full cell width, so names, dates
  // and phone numbers stay on one line instead of breaking mid-word.
  const Field = ({ label, value }) => (
    <div className="min-w-0">
      <p className="text-[11px] leading-4 text-darkgrey opacity-70">{label}</p>
      <p className="text-bodySBB text-darkgrey break-words">{value || "-"}</p>
    </div>
  );

  const tags = patientProfile?.tag || [];
  const patientType = patientProfile?.patient_type || [];
  const specializationType = patientProfile?.specialization_type || [];
  const showPatientTypeRow =
    configuration === "custom1" && showPatientType && patientType.length > 0;
  const showSpecializationRow =
    configuration === "custom2" &&
    showSpecializationType &&
    specializationType.length > 0;

  return (
    <div className="shadow-card rounded-15 pb-2">
      <div
        className={`shadow-card rounded-tl-15 rounded-tr-15 bg-secondary p-2 lg:p-3 pl-4 text-bodyBB text-darkgrey mb-2 lg:mb-4`}
      >
        Patient Information
      </div>

      {/* ===== Mobile (< lg) — avatar + name/ID, then a 2-col grid ======= */}
      <div className="lg:hidden px-3">
        <div className="flex gap-3 items-center">
          {!hideProfilePic && (
            <img
              src={
                patientProfile?.profilePicture ? img : "/default_pro_pic.png"
              }
              className="w-[64px] h-[64px] min-w-[64px] rounded-full shadow-sm border-2 object-cover"
              alt="Profile pic"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-bodyBB text-darkgrey break-words">
              {patientProfile?.name || "-"}
            </p>
            <p className="text-bodySRB text-darkgrey opacity-70">
              ID:{" "}
              {hospitalInfo?.patientId_prefix + patientProfile?.patient_id ||
                "-"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
          <Field label="Mobile" value={patientProfile?.phone} />
          <Field label="Gender" value={patientProfile?.gender} />
          <Field
            label="Date of Birth"
            value={displayDate(patientProfile?.dob) || null}
          />
          <Field label="Age" value={patientProfile?.age} />
        </div>

        {(tags.length > 0 && showTag) ||
        showPatientTypeRow ||
        showSpecializationRow ? (
          <div className="mt-3 pt-3 border-t flex flex-col gap-2">
            {tags.length > 0 && showTag && (
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-bodySRB text-darkgrey opacity-70 mr-1">
                  Tags
                </span>
                {tags.map((item, index) => (
                  <span
                    className="p-1 text-xs rounded-md min-w-max"
                    style={{
                      backgroundColor: tagColors[index % tagColors.length],
                    }}
                    key={index}
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
            {showPatientTypeRow && (
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-bodySRB text-darkgrey opacity-70 mr-1">
                  Patient type
                </span>
                {patientType.map((item, index) => (
                  <span
                    className="bg-[#FFC300] p-1 text-xs rounded-md min-w-max"
                    key={index}
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
            {showSpecializationRow && (
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-bodySRB text-darkgrey opacity-70 mr-1">
                  Specialization
                </span>
                {specializationType.map((item, index) => (
                  <span
                    className="bg-[#FFC300] p-1 text-xs rounded-md min-w-max"
                    key={index}
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* ===== Tablet / desktop (≥ lg) — original layout preserved ======= */}
      <div className="hidden lg:flex px-4">
        <div className="lg:flex gap-11">
          {!hideProfilePic && (
            <div className="flex justify-center">
              <img
                src={
                  patientProfile?.profilePicture ? img : "/default_pro_pic.png"
                }
                className="min-w-[122px] max-w-[122px] h-[122px] rounded-full shadow-sm border-4"
                alt="Profile pic"
              />
            </div>
          )}
          <div>
            <dl>
              <div className="px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-bodyLB text-darkgrey">Patient ID</dt>
                <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2">
                  {hospitalInfo?.patientId_prefix +
                    patientProfile?.patient_id || "-"}
                </dd>
              </div>
              <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-bodyLB text-darkgrey">Patient Name</dt>
                <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2">
                  {patientProfile?.name || "-"}
                </dd>
              </div>
              <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-bodyLB text-darkgrey">Mobile Number</dt>
                <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2">
                  {patientProfile?.phone || "-"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div>
          <div>
            <dl>
              <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-bodyLB text-darkgrey">Gender</dt>
                <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2">
                  {patientProfile?.gender || "-"}
                </dd>
              </div>
              <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-bodyLB text-darkgrey">Date of Birth</dt>
                <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2">
                  {displayDate(patientProfile?.dob) || "-"}
                </dd>
              </div>
              <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-bodyLB text-darkgrey">Age</dt>
                <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2">
                  {patientProfile?.age || "-"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div>
          {tags.length > 0 && showTag && (
            <div>
              <dl>
                <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-0 xl:px-6 items-center">
                  <dt className="text-bodyLB text-darkgrey">Tags</dt>
                  <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2 flex flex-wrap gap-1">
                    {tags.map((item, index) => (
                      <span
                        className=" p-1 flex gap-1 items-center min-w-max rounded-md text-sm"
                        style={{
                          backgroundColor: tagColors[index % tagColors.length],
                        }}
                        key={index}
                      >
                        {item}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>
            </div>
          )}
          {configuration !== "standard" && showPatientType && (
            <>
              {showPatientTypeRow && (
                <div>
                  <dl>
                    <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-0 xl:px-6 items-center">
                      <dt className="text-bodyLB text-darkgrey">Patient type</dt>
                      <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2 flex flex-wrap gap-1">
                        {patientType.map((item, index) => (
                          <span
                            className="bg-[#FFC300] p-1 flex gap-1 items-center min-w-max rounded-md text-sm"
                            key={index}
                          >
                            {item}
                          </span>
                        ))}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
              {showSpecializationRow && (
                <div>
                  <dl>
                    <div className=" px-4 py-2 grid grid-cols-2 sm:grid-cols-3 sm:gap-4 sm:px-0 xl:px-6 items-center">
                      <dt className="text-bodyLB text-darkgrey">
                        Specialization type
                      </dt>
                      <dd className="mt-1 text-bodyRB text-darkgrey sm:mt-0 sm:col-span-2 flex flex-wrap gap-1">
                        {specializationType.map((item, index) => (
                          <span
                            className="bg-[#FFC300] p-1 flex gap-1 items-center min-w-max rounded-md text-sm"
                            key={index}
                          >
                            {item}
                          </span>
                        ))}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
